import { mergeClassNames } from '@/utils/mergeClassNames';
import React, { useContext, useEffect, useRef, useState } from 'react';
import type { TooltipProps as ReactAriaTooltipProps } from 'react-aria-components';
import {
  Tooltip as ReactAriaTooltip,
  TooltipTrigger,
  TooltipTriggerStateContext,
} from 'react-aria-components';
import { FocusableTrigger } from './FocusableTrigger';
import styles from './Tooltip.module.scss';

// (!!!) Make sure to update these values when you edit the CSS
//
// Ideally we would compute these from the CSS variables, but they're in rems
// and it would be tough to resolve them into pixel values without doing some
// hacky code to render offscreen or invisible elements and then measuring its
// size.
const LINE_HEIGHT = 24;
const SINGLE_LINE_PADDING_Y = 4;
const SINGLE_LINE_HEIGHT = LINE_HEIGHT + SINGLE_LINE_PADDING_Y * 2;

export const TOOLTIP_PLACEMENTS: ReactAriaTooltipProps['placement'][] = [
  'bottom',
  'bottom left',
  'bottom right',
  'top',
  'top left',
  'top right',
  'left',
  'left top',
  'left bottom',
  'right',
  'right top',
  'right bottom',
];

const OFFSET_VALUES = {
  small: 4,
  medium: 8,
  large: 12,
};

const getOffsetValue = (offset: TooltipProps['offset'] = 'medium'): number => {
  if (typeof offset === 'number') {
    return offset;
  }

  return OFFSET_VALUES[offset];
};

export type TooltipProps =
  // Explicitly omits:
  // - `offset` because otherwise the ReactAriaTooltipProps `offset` type takes precedence.
  // - `children` because otherwise the JSDoc comment from RAC get combined with ours.
  Omit<ReactAriaTooltipProps, 'children' | 'offset'> & {
    /** Whether the tooltip content can extend to multiple lines. */
    allowMultiline?: boolean;
    /** The trigger to toggle the tooltip. Must be either:
     * - `Button` or `IconButton` from `@zapier/design-system@9.x`
     * - a focusable React Aria component
     * */
    children: React.ReactNode;
    /** The content inside the tooltip. */
    content: string;
    /** The additional offset applied along the main axis between the element and its anchor element. */
    offset?: 'small' | 'medium' | 'large' | number;
  };

/**
 * Displays a description of an element while hovering over or focusing on that
 * element. Built using React Aria's
 * [Tooltip](https://react-spectrum.adobe.com/react-aria/Tooltip.html)
 * component.
 */
export const Tooltip = ({
  allowMultiline = false,
  children,
  className,
  content,
  isOpen,
  offset = 'medium',
  onOpenChange,
  placement = 'bottom',
  style,
}: TooltipProps) => {
  return (
    <TooltipTrigger
      closeDelay={200}
      delay={200}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {children}
      <TooltipContent
        allowMultiline={allowMultiline}
        className={className}
        content={content}
        offset={offset}
        placement={placement}
        style={style}
      />
    </TooltipTrigger>
  );
};

type TooltipContentProps = Required<
  Pick<TooltipProps, 'allowMultiline' | 'content' | 'offset' | 'placement'>
> &
  Pick<TooltipProps, 'className' | 'style'>;

/**
 * Wrapper around Tooltip content. It calculates the Tooltip's height to apply
 * the correct styles to multi-line content.
 * @private
 */
const TooltipContent = ({
  allowMultiline,
  className,
  content,
  offset,
  placement,
  style,
}: TooltipContentProps) => {
  const state = useContext(TooltipTriggerStateContext);
  const isOpen = state?.isOpen ?? false;

  const [isMultiline, setIsMultiLine] = useState(allowMultiline);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!allowMultiline) {
      setIsMultiLine(false);
    } else if (tooltipRef.current) {
      const tooltipHeight = tooltipRef.current.getBoundingClientRect().height;
      const isMultiLine = tooltipHeight > SINGLE_LINE_HEIGHT;
      setIsMultiLine(isMultiLine);
    }
  }, [isOpen, allowMultiline]);

  return (
    <ReactAriaTooltip
      className={(renderProps) =>
        mergeClassNames([styles.tooltip, className], renderProps)
      }
      // These kind of feel like they should be mutually exclusive, but there's
      // actually three states here:
      //
      // - multi-line NOT allowed
      // - multi-line allowed (current lines = 1)
      // - multi-line allowed (current lines > 1)
      //
      // The current implementation uses a different line height depending on
      // whether the content is actually line wrapping or not. In the future we
      // could consider simplifying this to two cases, to avoid runtime
      // measurement of child boxes. The visual difference would be incredibly
      // subtle, but the code would be faster and simpler to maintain.
      data-single-line={!allowMultiline}
      data-multi-line={isMultiline}
      data-zds
      offset={getOffsetValue(offset)}
      placement={placement}
      ref={tooltipRef}
      style={style}
    >
      {content}
    </ReactAriaTooltip>
  );
};

Tooltip.FocusableTrigger = FocusableTrigger;

export default Tooltip;
